import {
  assignSettinThrottleQuickAdds,
  getSettingThrottledQuickAdds,
} from "@faire/retailer-visitor-shared/settings/getSettingEnableThrottledQuickAdds";
import {
  ICartItemForUpdate,
  UpdateCartHandler,
} from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { AddToCartVariants } from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { trackEvent } from "@faire/web/common/trackEvent";
import { useCallback, useRef } from "react";

export const OPTIMIZED_BULK_UPDATE_DEBOUNCE_THREHOLD = 500;

/**
 * A hook for handling bulk updates to cart items with debouncing
 *
 * @param updateCart - The function to update the cart
 * @param container - The container identifier for tracking events
 * @returns A function to update cart items in bulk
 */
export const useBulkUpdateCartItem = (
  updateCart: UpdateCartHandler | undefined,
  container: string
) => {
  // Use refs to store the current state to avoid closure issues with debounced functions
  const pendingUpdatesRef = useRef<Record<string, ICartItemForUpdate>>({});
  const currentVariantRef = useRef<AddToCartVariants | undefined>(undefined);
  const currentOnSubmitRef = useRef<(() => void) | undefined>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);

  // Create a stable debounced function that persists across renders
  const debouncedUpdateCartRef = useRef<(() => void) | null>(null);

  // Initialize the debounced function if it doesn't exist
  if (!debouncedUpdateCartRef.current) {
    debouncedUpdateCartRef.current = debounceIfPossible(
      () => {
        if (!updateCart) {
          return;
        }
        assignSettinThrottleQuickAdds();

        // Get the current updates
        const updates = { ...pendingUpdatesRef.current };
        const variant = currentVariantRef.current;
        const onSubmit = currentOnSubmitRef.current;
        const timeOfLastAddToCartClick = lastUpdateTimeRef.current;

        // Clear the pending updates
        pendingUpdatesRef.current = {};

        // Track events for each item
        Object.values(updates).forEach((item) => {
          trackEvent(
            `/container/${container}/quick_add/${item.product_option_token ?? ""}`
          );
        });

        // Update the cart with all the items
        updateCart(Object.values(updates), {
          upltParams: {
            event: "add-to-cart",
            startTime: timeOfLastAddToCartClick,
            variant: variant || AddToCartVariants.QUICK_ADD_BTN,
          },
        });

        // Call the onSubmit callback if provided
        onSubmit?.();
      },
      OPTIMIZED_BULK_UPDATE_DEBOUNCE_THREHOLD,
      { leading: getSettingThrottledQuickAdds() }
    );
  }

  // Return the bulkUpdateItems function
  const bulkUpdateItems = useCallback(
    (
      productToken: string,
      productOptionToken: string,
      value: number,
      brandToken: string,
      variant: AddToCartVariants,
      onSubmit?: () => void
    ) => {
      // Create a key for the update
      const key = `${productToken}-${productOptionToken}`;

      // Update the pending updates
      pendingUpdatesRef.current = {
        ...pendingUpdatesRef.current,
        [key]: ICartItemForUpdate.build({
          product_token: productToken,
          product_option_token: productOptionToken,
          quantity: value < 0 ? 0 : value,
          brand_token: brandToken,
        }),
      };

      // Update the current variant
      currentVariantRef.current = variant;

      // Update the last update time
      lastUpdateTimeRef.current = performance.now();

      // If there's a custom onSubmit for this specific update, use it
      if (onSubmit) {
        currentOnSubmitRef.current = onSubmit;
      }

      // Trigger the debounced function
      if (debouncedUpdateCartRef.current) {
        debouncedUpdateCartRef.current();
      }
    },
    []
  );

  return bulkUpdateItems;
};
